import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import './Home.css';

import ProjectCard from '../components/ProjectCard';
import ProjectModal from '../components/ProjectModal';
import ExperienceModal from '../components/ExperienceModal';
import Timeline from '../components/Timeline';
import LandingLogo from '../components/LandingLogo';

import aboutPhoto from '../assets/about/about_photo.png';
import kerropi from '../assets/about/miffy.gif';

// logos
import AWSLogo from '../assets/experiences/aws-logo.png';
import SalesforceLogo from '../assets/experiences/salesforce-logo.png'; 
import CornellLogo from '../assets/experiences/Break-Through-Tech-logo.png'; 
import IntegemLogo from '../assets/experiences/integem-logo.png';
import CENG from '../assets/experiences/CENG.png';

//photos
import team from '../assets/experiences/team.png';
import research from '../assets/experiences/research-poster.png';

// projects
import AWSProj from '../assets/projects/nyc-chatbot.png';
import K12CoTeacher from '../assets/projects/k12-coteacher.png';
import HousingRecords from '../assets/projects/manufactured_homes.png';
import CSAIMerch from '../assets/projects/csai-merch.png';
import JukeBoxd from '../assets/projects/jukeboxd.png';
import Spotify from '../assets/projects/spotify-rec.png';

const Home = () => {
  const [selectedProject, setSelectedProject] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedExperience, setSelectedExperience] = useState(null);
  const [isExperienceModalOpen, setIsExperienceModalOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const section = params.get('section');
    if (!section) return;
    const scrollTo = {
      about: () => document.getElementById('about')?.scrollIntoView({ behavior: 'smooth' }),
      experience: () => document.querySelector('.timeline')?.scrollIntoView({ behavior: 'smooth' }),
      projects: () => document.getElementById('projects')?.scrollIntoView({ behavior: 'smooth' }),
    };
    setTimeout(() => scrollTo[section]?.(), 0);
  }, [location.search]);

  const experiencesData = [
    {
      role: "Software Engineer Intern",
      company: "AWS Digital Transformation Hub",
      date: "Jan 2025 - Present",
      logo: AWSLogo,
      description: "As a Software Engineer Intern at the AWS Digital Transformation Hub (an AWS Cloud Innovation Center @ Cal Poly), I design and deliver scalable, cloud-based solutions for public sector partners such as the NYC Department of Education, SLO City, Contra Costa County, and California Public Schools. Beyond development, I've also been invited to speak at conferences such as the 2025 GlobalMindED and the 2025 Futures Summit, and mentored several winning teams at AWS AI Summer Camp.",
      detailedDescription: "",
      skills: ["AWS Lambda", "Amazon EC2", "Amazon Bedrock", "AWS CDK", "DynamoDB", "Textract", "Athena/Glue", "QuickSight", "Docker", "ECS", "API Gateway"],
      images: [
        {
          src: team,
          alt: "Team Photo"
        }
      ]
    },
    {
      role: "AI Studio Fellow (Tech Lead)",
      company: "Salesforce",
      date: "Aug 2025 - Present",
      logo: SalesforceLogo,
      description: "Leading a 6-person team through Break Through Tech AI Program to design NLP + time-series pipeline using FinBERT for financial news sentiment analysis and stock trend prediction.",
      detailedDescription: "",
      skills: ["FinBERT", "NLP", "Time-Series Analysis", "Team Leadership", "Financial Modeling"],
      images: []
    },
    {
      role: "Break Through Tech AI Fellow",
      company: "Cornell Tech",
      date: "Apr 2025 - Present",
      logo: CornellLogo,
      description: "Selected from 3,600+ applicants for year-long fellowship supporting women in AI, ML, and Data Science. Built and evaluated 4+ ML pipelines from raw data.",
      detailedDescription: "The Break Through Tech AI Fellowship has been transformative for my career in AI/ML. Through hands-on projects and mentorship from industry experts, I've gained practical experience in building production-ready ML systems and learned best practices for responsible AI development.",
      skills: ["Scikit-learn", "Pandas", "NumPy", "TesorFlow", "ML Pipelines", "Feature Engineering", "Model Evaluation", "Model Deployment", "NLP"],
      images: []
    },
    {
      role: "AI Intern/ Site Director",
      company: "Integem",
      date: "May 2024 - Aug 2024",
      logo: IntegemLogo,
      description: "Oversaw technical operations as site director, troubleshooting IoT robotics systems and real-time computer vision pipelines on NVIDIA Jetson Nano with Docker and MicroPython. Taught K-12 students how to apply AI, computer vision, and robotics concepts through hands-on projects while managing daily site logistics.",
      detailedDescription: "",
      skills: ["Computer Vision", "Deep Learning", "NVDIA Jetson Nano", "Docker", "K12-Module", "Object Recognition", "Robotics"],
      images: []
    },
    {
      role: "Machine Learning Student Researcher",
      company: "Cal Poly College of Engineering",
      date: "Jul 2023 - Oct 2023",
      logo: CENG,
      description: "Conducted literature review on AI/ML biases in government, business, tech, and public health sectors. Co-developed a DEI framework identifying best practices for bias reduction.",
      detailedDescription: "Focused on understanding and mitigating AI/ML biases across various sectors through comprehensive literature review. Collaborated on developing a diversity, equity, and inclusion framework that identifies actionable best practices for reducing algorithmic bias in machine learning systems.",
      skills: ["GANs", "CNN", "Research", "Literature Review", "AI Ethics", "Bias Mitigation", "DEI Framework Development"],
      images: [{src : research, alt: "A summative poster of findings for mitigating bias in Machine Learning and Robotics."}]
    }
  ];

  const projectsData = {
    aws: {
      screenshot: AWSProj,
      companyLogo: AWSLogo,
      roleTitle: "Software Engineer Intern - AWS Digital Transformation Hub (DxHub)",
      projectHeadline: "NYC DOE Multimodal RAG Chatbot",
      projectDescription: "Scaled Tier-1 support for 150K+ NYC DOE employees by deploying a serverless multimodal RAG chatbot, cutting response times from hours to seconds.",
      detailedDescription: "Built and deployed a multimodal RAG chatbot for the NYC Department of Education (largest public school system in the US!) that retrieves answers from training videos, PDFs, and DOCX, and DOE handbooks. The system which supports 150,000+ DOE staff, reduced helpdesk response times from hours to seconds, and cut manual video review by ~80%. After delivery, it was transferred into DOE's AWS account for ongoing production scaling.",
      tags: ['AWS CDK', 'AWS Lambda', 'DynamoDB', 'RAG', 'Multimodal AI', 'Amazon Bedrock', 'ECS', 'API Gateway'],
      githubUrl: "https://github.com/cal-poly-dxhub/doe-chatbot",
      demoUrl: null,
      demoVideo: "https://www.youtube.com/embed/dCdJ-PGCmVg?si=XrT8brZBYcpDEFNc"
    },
    k12coteacher: {
      screenshot: K12CoTeacher,
      companyLogo: AWSLogo,
      roleTitle: "Software Engineer Intern - AWS Cloud Innovation Center",
      projectHeadline: "K-12 AI Co-Teacher Assistant",
      projectDescription: "AI-powered classroom assistant helping teachers generate differentiated lesson plans for diverse learners, saving 3-4 hours/week per teacher.",
      detailedDescription: "In K-12 classrooms, teachers are responsible for meeting the diverse needs of all learners ranging from students receiving special education services and those with 504 accommodations, to English language learners and gifted and talented students who require enrichment. This solution provides an AI-powered classroom assistant that helps teachers quickly generate differentiated lesson plans and respond to student needs. Using Individual Education Plans (IEPs), 504 plans, and evaluation reports the solution extracts and highlights concise modifications to an existing lesson plan for the teacher. AWS Lambda and Amazon API Gateway provide backend application logic, while Amazon DynamoDB stores student profiles, class rosters, teacher notes, and chat history. Authentication and access are managed with Amazon Cognito, and the application is deployed with AWS Amplify.",
      tags: ['AWS Lambda', 'API Gateway', 'DynamoDB', 'Cognito', 'Amplify', 'AI/ML', 'Education Tech'],
      githubUrl: "https://github.com/cal-poly-dxhub/k12-co-teacher",
      demoUrl: null,
      demoVideo: null
    },
    housingrecords: {
      screenshot: HousingRecords,
      companyLogo: AWSLogo,
      roleTitle: "Software Engineer Intern - AWS Cloud Innovation Center",
      projectHeadline: "Manufactured Housing Records Automation",
      projectDescription: "ML-enabled web application using AWS Textract to automate extraction of housing data from PDF documents, reducing processing time by 96%.",
      detailedDescription: "Developed a lightweight, ML-enabled web application for the City of San Luis Obispo to automate extraction of vital housing records from state-issued PDF documentation. The solution leverages AWS Textract to intelligently transform key information from title and registration records into actionable data, including decal numbers, manufacturer details, serial numbers, and sale/transfer information. Built with Streamlit frontend and custom Python processing scripts, the system handles batch uploads, supports CSV exports, and gracefully manages incomplete documents. This innovation cuts document processing time from minutes to seconds while improving data reliability and freeing up staff for higher-value tasks.",
      tags: ['AWS Textract', 'Streamlit', 'Python', 'Document Processing', 'ML', 'Public Sector'],
      githubUrl: "https://github.com/cal-poly-dxhub/manufactured-homes",
      demoUrl: "https://dxhub.calpoly.edu/challenges/automating-housing-record-extraction-for-city-of-slo/",
      demoVideo: "https://www.youtube.com/embed/yG_n5Ze8GUg?si=6lC413J2YjAtYFE4"
    },
    csaimerch: {
      screenshot: CSAIMerch,
      roleTitle: "Personal Project",
      projectHeadline: "Computer Science + AI Club Merch Portal",
      projectDescription: "Full-stack e-commerce portal using SvelteKit and Cloud Firestore, streamlining order processing for 300+ club members and boosting sales by 57%.",
      detailedDescription: "Designed and developed a full-stack e-commerce portal using SvelteKit, CSS, Cloud Firestore, and Netlify, streamlining order processing and enhancing the purchasing experience for 300+ club members. Boosted club merchandise sales by 57% by optimizing user interface and improving order management efficiency.",
      tags: ['SvelteKit', 'Cloud Firestore', 'Netlify', 'CSS', 'E-commerce', 'Full-Stack'],
      githubUrl: 'https://github.com/calpoly-csai/CSAI-Website',
      demoUrl: 'https://csaicalpoly.com/merch-portal',
      demoVideo: null
    },
    spotify: {
      screenshot: Spotify,
      roleTitle: "Personal Project",
      projectHeadline: "Spotify Analyzer + Recommender System",
      projectDescription: "Aggregated 7M+ Spotify user records into an ML-ready dataset and developed a collaborative filtering recommendation system achieving 87% accuracy.",
      detailedDescription: "Coordinated 60+ participants through Spotify's 5-day request process to aggregate 7M+ user records. Transformed JSON exports into structured NumPy matrices for model training. Built collaborative filtering system using k-Nearest Neighbors and Pearson correlation with 87% accuracy. Enhanced recommendation diversity by applying DBSCAN clustering to discover 5 niche listener groups.",
      tags: ['Scikit-learn', 'Pandas', 'NumPy', 'KNN', 'DBSCAN', 'Collaborative Filtering'],
      githubUrl: 'https://github.com/shaaronl/spotify-kdd-recommender',
      demoUrl: null,
      demoVideo: null
    },
    jukeboxd: {
      screenshot: JukeBoxd,
      roleTitle: "Team Project (5 members)",
      projectHeadline: "JukeBox'd - Social Music Review Platform",
      projectDescription: "Improved backend reliability for a social music review platform by designing RESTful CRUD endpoints and implementing CI/CD pipelines.",
      detailedDescription: "Collaborated on the design and implementation of 18+ RESTful CRUD endpoints supporting user profiles, album/artist displays, and review features. Improved integration speed by ~40% by introducing CI/CD pipelines with GitHub Actions and Microsoft Azure, reducing manual build/deploy steps and streamlining team collaboration. Contributed to building a full-stack social platform for music reviews and recommendations.",
      tags: ['React', 'MongoDB', 'Express', 'Node.js', 'Azure', 'CI/CD', 'GitHub Actions'],
      githubUrl: 'https://github.com/shaaronl/jukeboxd',
      demoUrl: 'https://kind-pebble-01340fc1e.5.azurestaticapps.net/',
      demoVideo: null
    }
  };

  const openModal = (projectKey) => {
    setSelectedProject(projectsData[projectKey]);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedProject(null);
  };

  const openExperienceModal = (experience) => {
    setSelectedExperience(experience);
    setIsExperienceModalOpen(true);
  };

  const closeExperienceModal = () => {
    setIsExperienceModalOpen(false);
    setSelectedExperience(null);
  };
  return (
    <>
      <div className="section-container home-bg">
        <div className="home-header">
        <div className="home-name">
          <LandingLogo />
        </div>

        <div 
          className="intro-arrow" 
          onClick={() => {
            const about = document.getElementById('about');
            if (about) {
              about.scrollIntoView({ behavior: 'smooth' });
            }
          }}
        >⌄</div>
        </div>
      </div>
      <div id="about" className="section-container about-bg">
        <div className="about-section">
        <div className="about-content">
          <div className="about-image-block">
            <img
              src={aboutPhoto}
              alt="Sharon"
              className="about-main-img"
            />
          </div>
          <div className="about-text-block">
            <div className="text-greeting">
              <h1>About Me</h1>
              <img src={kerropi} alt="hi" className="wave-gif"/>
            </div>
            {/* <h2>About Me</h2> */}
            <p className="about-intro">
             Hi there! I'm Sharon Liang, a senior Computer Science student at  
              <a
                href="https://noyce.calpoly.edu/"
                target="_blank"
                rel="noopener noreferrer"
                className="home-status-link"
              > Cal Poly↗
              </a> specializing in AI/ML and minoring in Data Science. As a Software Engineer Intern at the
            <a
                href="https://dxhub.calpoly.edu/about/"
                target="_blank"
                rel="noopener noreferrer"
                className="home-status-link"
              > AWS-Cal Poly Digital Transformation Hub↗, 
              </a> I've delivered cloud solutions for public-sector clients such as the <b>NYC Department of Education</b>, California schools, and the City of San Luis Obispo, supporting over 150,000 users. I'm also a Tech Lead at
              <a
                href="https://www.salesforce.com/v3/"
                target="_blank"
                rel="noopener noreferrer"
                className="home-status-link"
              > Salesforce's 
              </a> Break Through Tech AI Studio, where I guide a team developing NLP and time-series pipelines for financial trend prediction.
              <br/><br/>
              With a background in dance and music, I'm especially drawn to the intersection of music ♫ and AI, exploring how recommender systems can make discovery more personal and emotionally resonant. I'm excited to apply my skills in AI/ML engineering, recommender systems, and scalable cloud applications to build technologies that bridge technical impact with human experience.
             <br/><br />
            </p>
          </div>
        </div>

        </div>
      </div>

      <div id="experience" className="section-container experience-bg">
        {/* <h2>Experiences</h2> */}
        <Timeline experiences={experiencesData} onExperienceClick={openExperienceModal} />
      </div>

      <div id="projects" className="section-container projects-bg">
        <h2 id="projects-title">Projects</h2>
        <div className="projects-grid">
          <ProjectCard
            screenshot={AWSProj}
            projectHeadline="NYC DOE Multimodal RAG Chatbot"
            // roleTitle="Project Lead"
            projectDescription="Scaled Tier-1 support for 150K+ NYC DOE employees by deploying a serverless multimodal RAG chatbot, cutting response times from hours to seconds."
            tags={['AWS CDK', 'Lambda', 'DynamoDB', 'RAG', 'Multimodal AI', 'API Gateway']}
            onClick={() => openModal('aws')}
          />
          <ProjectCard
            screenshot={Spotify}
            projectHeadline="Spotify Analyzer + Recommender System"
            projectDescription="Aggregated 7M+ Spotify user records into an ML-ready dataset and developed a collaborative filtering recommendation system achieving 87% accuracy."
            tags={['Scikit-learn', 'Pandas', 'NumPy', 'KNN', 'DBSCAN', 'Collaborative Filtering']}
            onClick={() => openModal('spotify')}
          />
          <ProjectCard
            screenshot={K12CoTeacher}
            projectHeadline="K-12 CoTeacher AI Assistant"
            projectDescription="AI-powered classroom assistant helping teachers generate differentiated lesson plans for diverse learners, saving 2-3 hours/week per teacher."
            tags={['AWS Lambda', 'API Gateway', 'DynamoDB', 'Cognito', 'Amplify', 'AI/ML', 'Education Tech']}
            onClick={() => openModal('k12coteacher')}
          />
          <ProjectCard
            screenshot={CSAIMerch}
            roleTitle="Personal Project"
            projectHeadline="Computer Science + AI Club Merch Portal"
            projectDescription="Full-stack e-commerce portal using SvelteKit and Cloud Firestore, streamlining order processing for 200+ club members and boosting sales by 27%."
            tags={['SvelteKit', 'Cloud Firestore', 'Netlify', 'CSS', 'E-commerce', 'Full-Stack']}
            onClick={() => openModal('csaimerch')}
          />
          <ProjectCard
            screenshot={JukeBoxd}
            roleTitle="Team Project (5 members)"
            projectHeadline="JukeBox'd - Social Music Review Platform"
            projectDescription="Improved backend reliability for a social music review platform by designing RESTful CRUD endpoints and implementing CI/CD pipelines."
            tags={['React', 'MongoDB', 'Express', 'Node.js', 'Azure', 'CI/CD', 'GitHub Actions']}
            onClick={() => openModal('jukeboxd')}
          />
          <ProjectCard
            screenshot={HousingRecords}
            projectHeadline="Manufactured Housing Records Automation"
            projectDescription="ML-enabled web application using AWS Textract to automate extraction of housing data from PDF documents, reducing processing time by 96%."
            tags={['AWS Textract', 'Streamlit', 'Python', 'Document Processing', 'ML', 'Public Sector']}
            onClick={() => openModal('housingrecords')}
          />
          </div>
          
      </div>
      <ProjectModal
        isOpen={isModalOpen}
        onClose={closeModal}
        project={selectedProject}
      />
      <ExperienceModal
        isOpen={isExperienceModalOpen}
        onClose={closeExperienceModal}
        experience={selectedExperience}
      />
    </>
  );
};

export default Home;
